/**
 * The dialog every screen in the app opens, from Radix; shadcn's wrapper with
 * the app's own changes (no exit animation, a visible close button, a
 * header that keeps room for it, buttons on one line, and an optional
 * scrolling body). Each is explained where it is made.
 *
 * A shadcn primitive at heart: update it from shadcn with care, keeping those.
 *
 * Used by: every dialog in the app (the plant form, photo dialogs, the
 * subscription wall and pause offer, the share and season review dialogs,
 * the profile's full-screen ProfileScreen, account deletion), and by
 * shared/components/ui/bottom-panel, which is built on it.
 *
 * Surprising: on a phone a dialog hangs from the top of the screen, not the
 * middle (see DialogContent), and the close button's label is translated,
 * so CloseX reads the app's i18n.
 *
 * @module components/ui/dialog
 */
import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"

import { cn } from "@/lib/utils"
import { CloseX } from "@/shared/components/ui/CloseX"

/** One dialog: open or closed, and what changes that. */
const Dialog = DialogPrimitive.Root

/** A control that opens the dialog. */
const DialogTrigger = DialogPrimitive.Trigger

/** Puts the dialog above the page, outside the tree it is declared in. */
const DialogPortal = DialogPrimitive.Portal

/** A control that closes the dialog. */
const DialogClose = DialogPrimitive.Close

/**
 * The closed-state animations are deliberately absent.
 *
 * Radix keeps a closing element mounted until its exit animation reports
 * animationend. With those classes present that event never arrived here, so
 * every dialog in the app stayed in the DOM after it closed — visible, on top
 * of the page, and still holding focus. With no exit animation to wait for,
 * Presence unmounts immediately. The opening animations are untouched.
 */
const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

/** Radix's content props, and whether the body scrolls by itself. */
interface DialogContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  /**
   * Whether this dialog's own body should be the scrolling part.
   *
   * True for anything that just lists its content and lets the dialog cap the
   * height. False for the few that build their own fixed header over their own
   * scrolling region — their children must stay direct children of the content
   * box, because they size themselves against it with flex-1.
   */
  scrollBody?: boolean;
}

/** The dialog's box over the overlay, with its close button. */
const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, scrollBody = true, ...props }, ref) => {
  return (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        /*
         * grid-cols-[minmax(0,1fr)] rather than a bare grid.
         *
         * A grid item's min-width is auto, so the single column was sized to
         * the widest thing inside it and the dialog grew to match — past its
         * own max-width, past the edge of the screen. The edit-plant dialog
         * did that on a phone: a line reading "Common Summer Savory · Standard
         * Common Summer Savory" carries `truncate`, which is white-space:
         * nowrap, and nowrap text has a min-content width of the whole line.
         * Nothing upstream was allowed to shrink, so nothing ever needed to
         * truncate, and 534px of content sat in a 373px box.
         *
         * minmax(0, 1fr) says the column may be narrower than its contents.
         * The shrinking then reaches the truncating line, which does its job.
         *
         * grid-rows gets the same treatment, for the same reason one axis over:
         * without it the single row is sized to its content, the scrolling
         * wrapper inside is handed that full height, and there is nothing left
         * for it to scroll — the overflow simply gets clipped by the box, and
         * the bottom of the form becomes unreachable rather than merely far
         * away. A row that may be shorter than its contents is what gives the
         * wrapper a height to scroll within.
         *
         * max-h-[90dvh] with overflow-y-auto, for the same reason one axis up.
         *
         * A dialog is centred with translate-y(-50%), so content taller than
         * the screen does not hang off the bottom where you might scroll to
         * it — it is cut off at the top and the bottom at once, symmetrically,
         * with no way to reach either end. Editing a location did that. Four
         * dialogs had remembered to cap their height and scroll; three had
         * not, and which group a dialog fell into was a matter of who wrote it.
         *
         * dvh rather than vh because a phone's address bar is part of the
         * viewport under vh and not under dvh, which is the difference between
         * a footer you can reach and one you cannot.
         *
         * A dialog that manages its own scrolling — a fixed header over a
         * scrolling body — says scrollBody={false} and keeps its children as
         * direct children; it may still override any of these classes, and
         * tailwind-merge lets the caller win.
         *
         * The scrolling moved off this box and onto a wrapper inside it,
         * because the close button is positioned against this box: while this
         * box was the scroller, the button scrolled away with the content and
         * a tall dialog lost its own close. Now the box stays put, the body
         * moves under it, and the button is where it was left.
         */
        /*
         * On a phone the dialog hangs from the top, not the middle.
         *
         * Phone browsers hide part of the bottom edge — a toolbar, an address
         * bar sliding in, a keyboard — and a dialog centred on the full
         * height puts its footer exactly there. Anchored near the top and
         * capped at 82dvh, the buttons stay above whatever the browser has
         * parked at the bottom. From sm up it is centred as before.
         */
        "fixed left-[50%] top-4 z-50 grid grid-cols-[minmax(0,1fr)] grid-rows-[minmax(0,1fr)] max-h-[82dvh] w-full max-w-lg translate-x-[-50%] gap-4 overflow-hidden border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:top-[50%] sm:max-h-[90dvh] sm:translate-y-[-50%] sm:rounded-lg",
        className
      )}
      {...props}
    >
      {scrollBody
        ? (
          // auto-rows-max: a row is as tall as its content, never less. Without
          // it, a child that scrolls on its own — a list of thumbnails, a
          // catalogue picker — was the one row the grid squeezed when the
          // dialog ran out of height, and it collapsed to a sliver while
          // everything around it kept its size. Now this wrapper scrolls.
          // A scroller clips at its edge, so a field flush with it lost its
          // border and its focus ring: -m-1 p-1 gives them 4px to draw in,
          // with nothing moving.
          <div className="-m-1 grid min-h-0 auto-rows-max grid-cols-[minmax(0,1fr)] gap-4 overflow-y-auto p-1">
            {children}
          </div>
        )
        : children}
      {/*
        * A button, not a faint glyph.
        *
        * This was a 16px X at 70% opacity with no background and no padding —
        * hard to see on a pale dialog, invisible over a photograph, and a tap
        * target well under what a thumb can reliably hit. It now wears the
        * same clothes as the camera and scroll buttons on the plant card:
        * bordered, on its own background, with a real hit area. That also
        * makes it legible wherever a dialog opens straight onto an image.
        */}
      <DialogPrimitive.Close asChild>
        <CloseX className="absolute right-3 top-3 z-20" />
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
  );
})
DialogContent.displayName = DialogPrimitive.Content.displayName

/**
 * A dialog's heading, with room kept for the close button.
 *
 * The close button is positioned against the dialog, not the header, so a
 * heading that runs the full width runs underneath it. That was survivable
 * while the button was a faint 16px glyph and stopped being so the moment it
 * became something you could actually see and hit. Reserved here rather than
 * in each dialog, and any header that needs more can still say so — cn merges
 * with later classes winning.
 */
const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 pr-8 text-center sm:text-left",
      className
    )}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

/**
 * A dialog's buttons, on one line at every width.
 *
 * shadcn stacks them on a phone — full-width targets, primary on top — which
 * is the better shape in isolation and costs fifty pixels of height. Fifty
 * pixels is what decides whether the dialog around it scrolls, and a dialog
 * that scrolls hides the very buttons the stacking was meant to make easier to
 * hit. Two buttons side by side at 375px are still about 170px each, which is
 * a comfortable target by any measure.
 */
const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-row justify-end gap-2",
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

/** The dialog's title, announced when it opens. */
const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

/** The line under the title, read out with it. */
const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
